import { Modal, Skeleton, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from 'src/components/button';
import { BaseText } from 'src/components/typography';
import {
  EHomeworkType,
  TActivityData,
  TGetHomeworkResultParams,
  TGetAiConversationParams,
  THomework,
  TTaskItemData,
  TUpdateAiConversationParams,
} from 'src/interfaces/clients-interface';
import { TFile } from 'src/interfaces/common-interface';
import { TRootState, useAppDispatch } from 'src/stores';
import {
  EClientsAction,
  getHomeworkResultAction,
  getAiConversationAction
} from 'src/stores/clients';
import ActivityCard from './ActivityCard';
import HomeworkActivityItem from './HomeworkActivityItem';
import HomeworkInfo from './HomeworkInfo';
import QuestionResults from './QuestionResults';
import './HomeworkHistoryDetailsModal.scss';
import { downloadHomeworkFile } from 'src/utils/common-utils';
import { EHomeworkResultStatus } from 'src/variables/enum-variables';
import {
  LikeFilled,
  LikeOutlined,
  DislikeFilled,
  DislikeOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { updateAiConversationDataAction } from 'src/stores/clients/clients-actions';
import { getSocket } from 'src/configs/socket-config';
import jsPDF from 'jspdf';
import AIConversationView from 'src/components/ai-conversation/AIConversationView';


interface IProps {
  open: boolean;
  data?: TActivityData;
  historyData?: TTaskItemData;
  onPreviewFiles?: (files: TFile[]) => void;
  onClose: () => void;
}

const HomeworkHistoryDetailsModal = ({
  open,
  data,
  historyData,
  onClose,
  onPreviewFiles,
}: IProps) => {
  const dispatch = useAppDispatch();
  const isFromAssigned = !!historyData;

  // State to track the index for showing the refined input or thank-you message
  const [refinedInputIndex, setRefinedInputIndex] = useState<number | null>(
    null,
  );
  const [submitted, setSubmitted] = useState<boolean[]>([]); // Tracks which indexes have submitted responses
  const socket = getSocket();

  const {
    homeworkDetails,
    homeworkResult,
    loading,
    downloading,
    clientId,
    clientName,
    aiConversationData,
  } = useSelector((state: TRootState) => ({
    homeworkDetails: state.clients.homeworkDetail,
    homeworkResult: state.clients.homeworkResult,
    loading: state.loading[EClientsAction.GET_HOMEWORK_RESULT],
    downloading: state.loading[EClientsAction.DOWNLOAD_HOMEWORK_FILE],
    clientId: state.clients.clientInfo?.clientId,
    clientName: state.clients.clientInfo?.firstName,
    aiConversationData: state.clients.aiConversationData,
  }));

  const homework = isFromAssigned
    ? homeworkResult?.homework
    : homeworkDetails?.homework;
  const formattedActivityData: TActivityData | undefined =
    homeworkResult && {
      id: homeworkResult.id,
      resultText: homeworkResult.clientResponse?.responseText,
      comment: homeworkResult.feedback,
      createdAt: homeworkResult.createdAt,
      filesUrls: homeworkResult.clientAnswerImages,
      homeworkType: homeworkResult.homework?.type,
      rate: homeworkResult.rate,
      rejectText: homeworkResult.rejectReason,
      status: homeworkResult.status,
      classification: homeworkResult.classification,
    };
  const activityData = data ?? formattedActivityData;
  const isSkipped = activityData?.status === EHomeworkResultStatus.SKIPPED;

  const handleExportToPDF = () => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxLineWidth = pageWidth - margin * 2;
    const lineHeight = 7;
    let yOffset = margin;

    // Centered title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const title = 'AI Conversation';
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - titleWidth) / 2, yOffset);
    yOffset += lineHeight * 2;

    // Append the conversation
    if (aiConversationData && aiConversationData.length > 0) {
      aiConversationData.forEach((message, index) => {
        const isUser = message.role === 'user';
        const role = isUser ? `${clientName}:` : 'jAImee:';
        const messageContent = message.content;

        // Prepare message text
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);

        const textLines = doc.splitTextToSize(messageContent, maxLineWidth - 20);
        const blockHeight = textLines.length * lineHeight + lineHeight * 2;

        // Check if new page is needed
        if (yOffset + blockHeight > pageHeight - margin) {
          doc.addPage();
          yOffset = margin;
        }

        // Draw a rectangle (chat bubble)
        const rectX = margin;
        const rectY = yOffset - lineHeight / 2;
        const rectWidth = maxLineWidth;
        const rectHeight = blockHeight;

        // Set background color
        const fillColor = isUser ? '#e6e6e6' : '#f0f0f0'; // Light shades of gray
        doc.setFillColor(fillColor);
        doc.roundedRect(rectX, rectY, rectWidth, rectHeight, 5, 5, 'F');

        // Add role text
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(
          role,
          rectX + 5,
          yOffset + lineHeight / 2,
          { maxWidth: rectWidth - 10 }
        );
        yOffset += lineHeight * 1.5;

        // Add message content
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(
          textLines,
          rectX + 5,
          yOffset,
          { maxWidth: rectWidth - 10 }
        );

        yOffset += blockHeight - lineHeight * 2;
        yOffset += lineHeight;
      });
    } else {
      doc.setFontSize(12);
      doc.text("No AI Conversation Data Available", margin, yOffset);
      yOffset += lineHeight;
    }

    doc.save(`conversation_${clientName}.pdf`);
  };


  const getHomeworkResult = (params: TGetHomeworkResultParams) => {
    dispatch(getHomeworkResultAction(params));
  };

  const getAiConversation = (params: TGetAiConversationParams) => {
    dispatch(getAiConversationAction(params));
  };

  const handleThumbsUpClick = (index: number) => {
    if (aiConversationData) {
      const conversationItem = aiConversationData[index];
      if (conversationItem.thumbsUp !== true && clientId) {
        const updatedAiConversationData = [...aiConversationData];
        updatedAiConversationData[index] = {
          ...conversationItem,
          thumbsUp: true,
        };

        const updatedParameters: TUpdateAiConversationParams = {
          messageId: aiConversationData[index].id,
          thumbsUp: true,
          clientId: clientId,
        };

        dispatch(updateAiConversationDataAction(updatedParameters));

        const newSubmitted = [...submitted];
        newSubmitted[index] = false;
        setSubmitted(newSubmitted);

        if (refinedInputIndex === index) {
          setRefinedInputIndex(null);
        }
      }
    }
  };

  const handleThumbsDownClick = (index: number) => {
    if (aiConversationData) {
      const conversationItem = aiConversationData[index];
      if (conversationItem.thumbsUp !== false && clientId) {
        const updatedAiConversationData = [...aiConversationData];
        updatedAiConversationData[index] = {
          ...conversationItem,
          thumbsUp: false,
        };

        const updatedParameters: TUpdateAiConversationParams = {
          messageId: aiConversationData[index].id,
          thumbsUp: false,
          clientId: clientId,
        };

        dispatch(updateAiConversationDataAction(updatedParameters));
        setRefinedInputIndex(index);
      }
    }
  };

  const handleDownloadFile = (attachmentId: string, fileName?: string) => {
    const homeworkAssignId = isFromAssigned
      ? historyData?.homeworkAssignId
      : homeworkDetails?.id;

    downloadHomeworkFile(
      {
        attachmentId,
        homeworkAssignId: homeworkAssignId ?? '',
        homeworkResultId: activityData?.id ?? '',
        fileName,
      },
      dispatch,
    );
  };

  useEffect(() => {
    let params: TGetHomeworkResultParams | undefined;
    if (historyData) {
      params = {
        id: historyData.homeworkAssignId ?? '',
        homeworkHistoryId: historyData.id ?? '',
      };

      if (historyData.homeworkAssignId && clientId) {
        const aiParams: TGetAiConversationParams = {
          homeworkAssignId: historyData.homeworkAssignId,
          clientId: clientId,
        };
        getAiConversation(aiParams);
      }
    }

    if (data) {
      params = {
        id: homeworkDetails?.id ?? '',
        homeworkHistoryId: data?.id ?? '',
      };
    }

    if (params) {
      getHomeworkResult(params);
    }
  }, [historyData, data]);

  const handleRefinedResponseSubmit = (
    index: number,
    additionalComments: string,
  ) => {
    if (aiConversationData && aiConversationData[index] && clientId) {
      const updatedParameters: TUpdateAiConversationParams = {
        messageId: aiConversationData[index].id,
        thumbsUp: false,
        clientId: clientId,
        additionalComments: additionalComments,
      };

      dispatch(updateAiConversationDataAction(updatedParameters));
    }

    const newSubmitted = [...submitted];
    newSubmitted[index] = true;
    setSubmitted(newSubmitted);
    setRefinedInputIndex(null);
  };

  // Use the reusable AIConversationView component
  const renderAiConversation = () => {
    if (!aiConversationData || aiConversationData.length === 0) {
      return <div>No AI Conversation Data Available</div>;
    }

    return (
      <div className="HomeworkHistoryDetailsModal__ai-conversation-container">
        <AIConversationView
          conversationData={aiConversationData}
          clientName={clientName}
          onThumbsUp={handleThumbsUpClick}
          onThumbsDown={handleThumbsDownClick}
          onSubmitRefinedResponse={handleRefinedResponseSubmit}
          showActions={true}
          conversationDate={aiConversationData[0]?.createdAt}
        />
      </div>
    );
  };

  return (
    <Modal
      className="HomeworkHistoryDetailsModal"
      open={open}
      width={1020}
      closable={false}
      footer={null}
      onCancel={onClose}
    >
      {loading ? (
        <>
          <Skeleton />
          <center>
            <Spin />
          </center>
        </>
      ) : (
        <>
          <BaseText
            type="title"
            textAlign="center"
            className="HomeworkHistoryDetailsModal__title"
          >
            {homework?.title}
          </BaseText>
          {isFromAssigned && <HomeworkInfo homework={homework as THomework} />}

          {activityData && (
            <HomeworkActivityItem data={activityData} isCard={false}>
              {homework?.type === EHomeworkType.QUESTIONNAIRES &&
                !isSkipped && <QuestionResults homework={homeworkResult} />}
              {homework?.type === EHomeworkType.AI && renderAiConversation()}
              {homework?.type !== EHomeworkType.QUESTIONNAIRES &&
                activityData.resultText &&
                !isSkipped && (
                  <ActivityCard
                    data={activityData}
                    type="result"
                    className="HomeworkHistoryDetailsModal__result"
                    isCard={false}
                    downloading={downloading}
                    onPreviewFiles={onPreviewFiles}
                    onDownload={handleDownloadFile}
                  />
                )}
              {(activityData.comment ||
                activityData.rejectText ||
                activityData.rate) && (
                  <ActivityCard
                    type="comment"
                    isCard={false}
                    className="HomeworkHistoryDetailsModal__comment"
                    data={{
                      comment: activityData.comment,
                      rate: activityData.rate,
                      rejectText: activityData.rejectText,
                      title: "Client's comment",
                      id: 'fake Id',
                      classification: activityData.classification,
                    }}
                  />
                )}
            </HomeworkActivityItem>
          )}
        </>
      )}
      {/* Button container */}
      <div className="HomeworkHistoryDetailsModal__footer">
        {homework?.type === EHomeworkType.AI && aiConversationData && aiConversationData.length >= 2 && (
          <Button
            type="primary"
            onClick={handleExportToPDF}
          >
            Export
          </Button>
        )}
        <Button
          type="default"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default HomeworkHistoryDetailsModal;
